import { INSIGHTS_PROXY_PROVIDERS } from "schemas/bedrockApi/insightsProxyProviders";
import * as z from "zod";

/** Data sources a query can read: the Replo pixel tables or an external provider's views. */
export const INSIGHTS_DATASETS = [
  "pixel",
  ...INSIGHTS_PROXY_PROVIDERS,
] as const;

export const insightsDatasetSchema = z.enum(INSIGHTS_DATASETS);

export type InsightsDataset = z.infer<typeof insightsDatasetSchema>;

/**
 * How each dataset presents itself: the heading pickers show for its fields,
 * and the identifier-safe token qualified placeholders use (provider keys like
 * `triple-whale` don't fit the token grammar). Keyed by dataset rather than by
 * provider so pixel is looked up the same way as everything else.
 */
export const INSIGHTS_DATASET_METADATA: Record<
  InsightsDataset,
  { label: string; placeholderAlias: string }
> = {
  pixel: { label: "Replo pixel", placeholderAlias: "pixel" },
  "triple-whale": { label: "Triple Whale", placeholderAlias: "tripleWhale" },
  contentsquare: { label: "Contentsquare", placeholderAlias: "contentsquare" },
  mixpanel: { label: "Mixpanel", placeholderAlias: "mixpanel" },
  lifetimely: { label: "Lifetimely", placeholderAlias: "lifetimely" },
  faire: { label: "Faire", placeholderAlias: "faire" },
  alia: { label: "Alia", placeholderAlias: "alia" },
  intelligems: { label: "Intelligems", placeholderAlias: "intelligems" },
  "meta-marketing-api": { label: "Meta Ads", placeholderAlias: "metaAds" },
  "google-analytics": {
    label: "Google Analytics",
    placeholderAlias: "googleAnalytics",
  },
  "google-search-console": {
    label: "Search Console",
    placeholderAlias: "searchConsole",
  },
  shopify: { label: "Shopify", placeholderAlias: "shopify" },
  roi: { label: "SEO ROI", placeholderAlias: "roi" },
};

export const dateRangePresetIdSchema = z.enum([
  "last_7_days",
  "last_14_days",
  "last_30_days",
  "last_90_days",
  "last_16_months",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "year_to_date",
  "custom",
]);

export type DateRangePresetId = z.infer<typeof dateRangePresetIdSchema>;

export const comparisonModeSchema = z.enum([
  "previous_period",
  "previous_year",
  "none",
]);

export type ComparisonMode = z.infer<typeof comparisonModeSchema>;

export const filterOperatorSchema = z.enum([
  "equals",
  "notEquals",
  "in",
  "notIn",
  "contains",
  "notContains",
  "startsWith",
  "isSet",
  "isNotSet",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
]);

export type FilterOperator = z.infer<typeof filterOperatorSchema>;

export const scopeFilterSchema = z.object({
  fieldKey: z.string().min(1).max(120),
  op: filterOperatorSchema,
  value: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.union([z.string(), z.number()])),
      z.null(),
    ])
    .optional(),
});

export type ScopeFilter = z.infer<typeof scopeFilterSchema>;

export const scopeBreakdownSchema = z.object({
  fieldKey: z.string().min(1).max(120),
  limit: z.number().int().positive().max(200).optional(),
});

export type ScopeBreakdown = z.infer<typeof scopeBreakdownSchema>;

/** Concrete date range from the dashboard date picker. */
export const scopeDateRangeSchema = z.object({
  presetId: dateRangePresetIdSchema,
  since: z.string(),
  until: z.string(),
  comparison: comparisonModeSchema,
});

export type ScopeDateRange = z.infer<typeof scopeDateRangeSchema>;

/**
 * The runtime scope chosen in the dashboard query builder bar. Deliberately
 * serialisable (no functions, no Dates) so it can key query caches, survive
 * HMR, be captured verbatim into a share snapshot, and ride in the dashboard
 * URL so refresh and shared links restore it. Never persisted back to the
 * dashboard file.
 */
export const activeDashboardScopeSchema = z.object({
  dateRange: scopeDateRangeSchema.nullable(),
  filters: z
    .array(
      scopeFilterSchema.extend({
        isProjectDefault: z
          .literal(true)
          .optional()
          .describe(
            "Marks an automatically seeded project domain, not a viewer selection.",
          ),
      }),
    )
    .max(20)
    .default([]),
  breakdown: scopeBreakdownSchema.nullable(),
});

export type ActiveDashboardScope = z.infer<typeof activeDashboardScopeSchema>;

export const dashboardScopeDefaultsSchema = z.object({
  dateRange: z
    .object({
      fieldKey: z.string().min(1).max(120).default("date"),
      defaultPreset: dateRangePresetIdSchema.default("last_30_days"),
      defaultComparison: comparisonModeSchema.default("previous_period"),
      allowComparisonChange: z.boolean().default(true),
      customRange: z
        .object({
          since: z.iso
            .date()
            .describe("First included calendar date (YYYY-MM-DD)."),
          until: z.iso
            .date()
            .describe("Last included calendar date (YYYY-MM-DD)."),
        })
        .refine((range) => range.since <= range.until, {
          message: "The start date must be on or before the end date.",
        })
        .optional()
        .describe(
          "Exact dates used when defaultPreset is custom. Relative presets ignore these dates.",
        ),
    })
    .nullish(),
  filters: z.array(scopeFilterSchema).max(20).default([]),
  breakdown: scopeBreakdownSchema.nullish(),
});

export type DashboardScopeDefaults = z.infer<
  typeof dashboardScopeDefaultsSchema
>;

/**
 * The `:scope.*` fragment names a query may interpolate. `compileScopePlaceholders`
 * builds its substitution table from this list, so the vocabulary authoring
 * validates against cannot drift from the one rendering accepts.
 */
export const INSIGHTS_SCOPE_FRAGMENT_NAMES = [
  "where",
  "breakdownExpr",
  "breakdownAlias",
  "breakdownGroupBy",
  "breakdownSelect",
] as const;

export type InsightsScopeFragmentName =
  (typeof INSIGHTS_SCOPE_FRAGMENT_NAMES)[number];

/** `:scope.dateRange.*` sub-names, available whenever a date range is active. */
export const INSIGHTS_SCOPE_DATE_RANGE_NAMES = [
  "since",
  "until",
  "bucketSeries",
  "currentBucketExpr",
  "currentCutoffBucket",
  "bucketLabel",
] as const;

export type InsightsScopeDateRangeName =
  (typeof INSIGHTS_SCOPE_DATE_RANGE_NAMES)[number];

/** `:scope.dateRange.*` sub-names that additionally need an active comparison. */
export const INSIGHTS_SCOPE_COMPARISON_NAMES = [
  "previousSince",
  "previousUntil",
  "previousBucketExpr",
] as const;

export type InsightsScopeComparisonName =
  (typeof INSIGHTS_SCOPE_COMPARISON_NAMES)[number];

const FRAGMENT_NAMES: ReadonlySet<string> = new Set(
  INSIGHTS_SCOPE_FRAGMENT_NAMES,
);

const DATE_RANGE_NAMES: ReadonlySet<string> = new Set([
  ...INSIGHTS_SCOPE_DATE_RANGE_NAMES,
  ...INSIGHTS_SCOPE_COMPARISON_NAMES,
]);

/** Every `:scope.dateRange.*` sub-name, for error messages that list the vocabulary. */
export const SCOPE_DATE_RANGE_NAME_LIST = [...DATE_RANGE_NAMES].join(", ");

/** Whether `name` names a `:scope.dateRange.<name>`, comparison-gated ones included. */
export function isScopeDateRangeName(name: string): boolean {
  return DATE_RANGE_NAMES.has(name);
}

/** Fresh per call: a shared global regex would carry `lastIndex` between uses. */
export function getScopeTokenPattern(): RegExp {
  return /:scope\.([a-z]\w*)(?:\.([a-z]\w*))?/gi;
}

export interface UnknownScopeToken {
  /** The token as written, e.g. `:scope.dateRange.yesterday`. */
  token: string;
  reason: string;
}

/**
 * `:scope.*` tokens no substitution can answer, so authoring catches a typo the
 * compiler would otherwise only throw on at render time. Comparison-gated names
 * pass (the active scope decides those), as do dataset qualifiers like
 * `:scope.where.pixel`, which only the query's own datasets can validate.
 */
export function findUnknownScopeTokens(sql: string): UnknownScopeToken[] {
  const unknown: UnknownScopeToken[] = [];
  for (const [token, namespace, qualifier] of sql.matchAll(
    getScopeTokenPattern(),
  )) {
    if (namespace === "dateRange") {
      if (qualifier === undefined || !isScopeDateRangeName(qualifier)) {
        unknown.push({
          token,
          reason: `Unknown scope placeholder "${token}". Valid sub-names under :scope.dateRange: ${SCOPE_DATE_RANGE_NAME_LIST}.`,
        });
      }
    } else if (namespace === undefined || !FRAGMENT_NAMES.has(namespace)) {
      unknown.push({
        token,
        reason: `Unknown scope placeholder "${token}". Valid names: ${INSIGHTS_SCOPE_FRAGMENT_NAMES.join(", ")}, dateRange.`,
      });
    }
  }
  return unknown;
}
